import React, { useState } from "react";
import { useTestimonials, Testimonial } from "../../hooks/useTestimonials";
import NavbarAbout from "../../components/NavbarAbout";
import { analytics, EVENT_FN } from "../../analytics";

// service_type slug → badge 색상 (slug 기준으로 통일)
const SERVICE_COLORS: Record<string, string> = {
  diagnosis:    "bg-blue-50 text-blue-700 border-blue-200",
  core:         "bg-green-50 text-green-700 border-green-200",
  signature:    "bg-purple-50 text-purple-700 border-purple-200",
  "meeting-30": "bg-orange-50 text-orange-700 border-orange-200",
  // legacy 한국어 fallback
  "진단":   "bg-blue-50 text-blue-700 border-blue-200",
  "핵심":   "bg-green-50 text-green-700 border-green-200",
  "시그니처": "bg-purple-50 text-purple-700 border-purple-200",
  "30분 미팅": "bg-orange-50 text-orange-700 border-orange-200",
};

function StarRow({ score }: { score: number | null }) {
  const stars = score !== null ? Math.round(score / 2) : 0;
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <svg
          key={i}
          viewBox="0 0 20 20"
          className={`w-4 h-4 ${i < stars ? "fill-amber-400 text-amber-400" : "fill-gray-200 text-gray-200"}`}
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
}

function Modal({ item, onClose }: { item: Testimonial; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-lg w-full p-8 relative max-h-[80vh] overflow-y-auto shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-5 right-5 text-gray-400 hover:text-gray-700 text-lg leading-none"
        >
          ✕
        </button>

        <div className="flex flex-wrap items-center gap-2 mb-5">
          {item.service_type && (
            <span
              className={`text-xs px-2.5 py-1 rounded-full font-medium border ${
                SERVICE_COLORS[item.service_type] ?? "bg-gray-100 text-gray-600 border-gray-200"
              }`}
            >
              {item.service_name_ko ?? item.service_type}
            </span>
          )}
          <StarRow score={item.quality_score} />
          {item.quality_score !== null && (
            <span className="text-sm text-gray-500 font-medium">
              {item.quality_score}/10
            </span>
          )}
        </div>

        {item.header && (
          <p className="font-semibold text-[#0A1A3E] text-base mb-3">{item.header}</p>
        )}

        <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-line">
          {item.content}
        </p>

        {(item.experience ?? item.title) && (
          <div className="mt-6 pt-4 border-t border-gray-100">
            <p className="text-xs text-gray-400">{item.experience ?? item.title}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function FilterTab({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-150 ${
        active
          ? "bg-main text-white"
          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
      }`}
    >
      {label}
    </button>
  );
}

const TestimonialsPage: React.FC = () => {
  const { testimonials, loading, avgScore } = useTestimonials();
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [activeModal, setActiveModal] = useState<Testimonial | null>(null);

  // 실제 데이터에서 서비스 필터 탭 동적 생성 (service_name_ko 기준)
  const filterOptions = Array.from(
    new Map(
      testimonials
        .filter((t) => t.service_name_ko)
        .map((t) => [t.service_type, t.service_name_ko!])
    ).entries()
  );

  const filtered =
    activeFilter === null
      ? testimonials
      : testimonials.filter((t) => t.service_type === activeFilter);

  return (
    <div className="min-h-screen bg-gray-50">
      <NavbarAbout />
      {/* Hero */}
      <div className="bg-white border-b border-gray-100 py-16 px-4 text-center">
        <p className="text-sm font-medium text-main mb-3 tracking-wide uppercase">
          Client Reviews
        </p>
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
          실제 클라이언트의 이야기
        </h1>
        <p className="text-gray-500 text-base mb-8">
          BeMe와 함께 영국 취업을 준비한 분들의 솔직한 후기입니다
        </p>

        {!loading && testimonials.length > 0 && (
          <div className="flex items-center justify-center gap-8">
            <div className="text-center">
              <p className="text-3xl font-bold text-gray-900">
                {avgScore.toFixed(1)}
                <span className="text-lg text-gray-400">/10</span>
              </p>
              <p className="text-xs text-gray-400 mt-1">평균 만족도</p>
            </div>
            <div className="w-px h-10 bg-gray-200" />
            <div className="text-center">
              <p className="text-3xl font-bold text-gray-900">{testimonials.length}</p>
              <p className="text-xs text-gray-400 mt-1">누적 후기</p>
            </div>
          </div>
        )}
      </div>

      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Filter */}
        <div className="flex flex-wrap gap-2 mb-8">
          <FilterTab
            key="all"
            label={`전체 (${testimonials.length})`}
            active={activeFilter === null}
            onClick={() => {
              setActiveFilter(null);
              analytics.track(EVENT_FN.testimonialFilter("전체"));
            }}
          />
          {filterOptions.map(([slug, nameKo]) => (
            <FilterTab
              key={slug}
              label={nameKo}
              active={activeFilter === slug}
              onClick={() => {
                setActiveFilter(slug);
                analytics.track(EVENT_FN.testimonialFilter(nameKo));
              }}
            />
          ))}
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex justify-center items-center py-24">
            <div className="w-8 h-8 border-2 border-main border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Empty */}
        {!loading && filtered.length === 0 && (
          <div className="text-center py-24 text-gray-400">
            아직 등록된 후기가 없습니다
          </div>
        )}

        {/* Cards Grid */}
        {!loading && filtered.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((item) => {
              const isLong = item.content.length > 180;
              const preview = isLong
                ? item.content.slice(0, 180).trimEnd() + "..."
                : item.content;

              return (
                <article
                  key={item.id}
                  className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 flex flex-col hover:shadow-md transition-shadow duration-200"
                >
                  {/* Top row: badge + score */}
                  <div className="flex items-center justify-between mb-4">
                    {item.service_type ? (
                      <span
                        className={`text-xs px-2.5 py-1 rounded-full font-medium border ${
                          SERVICE_COLORS[item.service_type] ??
                          "bg-gray-100 text-gray-600 border-gray-200"
                        }`}
                      >
                        {item.service_name_ko ?? item.service_type}
                      </span>
                    ) : (
                      <span />
                    )}
                    {item.quality_score !== null && (
                      <span className="text-sm font-semibold text-gray-700">
                        {item.quality_score}
                        <span className="text-gray-400 font-normal">/10</span>
                      </span>
                    )}
                  </div>

                  {/* Stars */}
                  <div className="mb-3">
                    <StarRow score={item.quality_score} />
                  </div>

                  {/* Headline */}
                  {item.header && <p className="font-semibold text-[#0A1A3E] text-sm mb-2">{item.header}</p>}

                  {/* Content */}
                  <p className="text-gray-700 text-sm leading-relaxed flex-1">
                    {preview}
                  </p>

                  {/* Footer */}
                  <div className="mt-5 pt-4 border-t border-gray-100 flex items-end justify-between gap-3">
                    {(item.experience ?? item.title) ? (
                      <p className="text-xs text-gray-400 leading-snug">{item.experience ?? item.title}</p>
                    ) : (
                      <span />
                    )}
                    {isLong && (
                      <button
                        onClick={() => {
                          setActiveModal(item);
                          analytics.track(EVENT_FN.testimonialReadMore(item.service_type));
                        }}
                        className="text-xs text-main font-semibold shrink-0 hover:underline underline-offset-2"
                      >
                        더 보기
                      </button>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>

      {activeModal && (
        <Modal item={activeModal} onClose={() => setActiveModal(null)} />
      )}
    </div>
  );
};

export default TestimonialsPage;
